import React from 'react';
import PropTypes from 'prop-types';
import { strings } from '../../../../localization';
import Switch from '../../../../components/common/Switch';
import ProgressOverlay from '../../../../components/common/ProgressOverlay';
import { UPDATE_SETTINGS_TIMEOUT } from '../GlobalSettings';

const IDLING_MIN_VAL_GRACE_PERIOD = 5; // minutes
const IDLING_MAX_VAL_GRACE_PERIOD = 60; // minutes

const EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_IMPERIAL = 0; 
const EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_IMPERIAL = 100; 

const EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC = 0;
const EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_METRIC = 160;

const msToMinutes = ms => ms !== undefined ? ms / (60 * 1000) : "";
const minutesToMs = min => min !== undefined ? min * 60 * 1000 : undefined;

export default class EventsIdling extends React.PureComponent {

    static propTypes = {
        eventConfigAvailable: PropTypes.bool,
        eventAvailabilityPending: PropTypes.bool.isRequired,
        eventAvailabilityEnabled: PropTypes.bool,
        eventAvailabilityErrorMessage: PropTypes.string,
        applyEventAvailability: PropTypes.func.isRequired,
        timeoutEventAvailability: PropTypes.func.isRequired,

        eventThresholdPending: PropTypes.bool.isRequired,
        eventThreshold: PropTypes.number,
        eventThresholdErrorMessage: PropTypes.string,
        applyEventThreshold: PropTypes.func.isRequired,
        timeoutEventThreshold: PropTypes.func.isRequired,

        eventSpeedThresholdPending: PropTypes.bool.isRequired,
        eventSpeedThreshold: PropTypes.number,
        eventSpeedThresholdErrorMessage: PropTypes.string,
        applyEventSpeedThreshold: PropTypes.func.isRequired,
        timeoutEventSpeedThreshold: PropTypes.func.isRequired,

        expandOrCollapseStateForUIReferences: PropTypes.func.isRequired,
        eventsIdlingExpandReference: PropTypes.string.isRequired,
        eventsIdlingExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        unitOfMeasureDistanceIsMetric: PropTypes.bool        
    }

    constructor(props) {
        super(props);

        this.state = {
            eventThreshold: undefined,
            eventThresholdValidationMessage: undefined,

            eventSpeedThreshold: undefined,
            eventSpeedThresholdValidationMessage: undefined
        };

        this.applyEventAvailabilityTimeoutId = undefined;
        this.applyEventThresholdTimeoutId = undefined;
        this.applyEventSpeedThresholdTimeoutId = undefined;
    }

    componentWillUnmount() {
        clearTimeout(this.applyEventAvailabilityTimeoutId);
        clearTimeout(this.applyEventThresholdTimeoutId);
        clearTimeout(this.applyEventSpeedThresholdTimeoutId);
    }

    handleThresholdChange = (event) => {
        let trimmedValue = event.target.value ? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
                
        if (trimmedValue === null) {
            this.setState({
                eventThreshold: trimmedValue // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            });
            return;    
        }

        if (isNaN(trimmedValue)) { return; }

        const numValue = Number(trimmedValue);
        let eventThresholdValidationMessage = undefined;

        if (numValue < IDLING_MIN_VAL_GRACE_PERIOD || numValue > IDLING_MAX_VAL_GRACE_PERIOD) {
            eventThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_minutes, IDLING_MIN_VAL_GRACE_PERIOD, IDLING_MAX_VAL_GRACE_PERIOD);
        }

        // check if need to clear the "apply" button when the value is changed back to the value in the cloud
        const sanitizedNumberRounded = numValue.toFixed(1); // rounded to whole number
        const eventThresholdRounded = msToMinutes(this.props.eventThreshold).toFixed(1);

        if (sanitizedNumberRounded === eventThresholdRounded) {
            const indexOfDecimal = trimmedValue.indexOf(".");
            if (indexOfDecimal === -1 ||
                indexOfDecimal < trimmedValue.length - 1) { // allow trailing decimal during input
                this.setState({
                    eventThreshold: undefined,
                    eventThresholdValidationMessage: undefined
                });
                return;
            }
        }

        this.setState({
            eventThreshold: numValue, 
            eventThresholdValidationMessage: eventThresholdValidationMessage
        });
    }

    handleSpeedThresholdChange = (event) => {
        const trimmedValue = event.target.value ? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
        
        if (trimmedValue === null) {
            this.setState({
                eventSpeedThreshold: trimmedValue // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            });
            return;    
        }

        if (isNaN(trimmedValue)) { return; }

        const indexOfDecimal = trimmedValue.indexOf(".");
        if (indexOfDecimal !== -1) {
            if (indexOfDecimal < trimmedValue.length - 2) {// .01 || 0.01
                return; // .0 || 0.0
            }
        }

        const numValue = Number(trimmedValue);

        let eventSpeedThresholdValidationMessage = undefined;
        if (this.props.unitOfMeasureDistanceIsMetric !== true) {
            if (numValue < EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_IMPERIAL || numValue > EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_IMPERIAL) { 
                eventSpeedThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_mp_h, EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_IMPERIAL, EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_IMPERIAL);
            }
        } else {
            if (numValue < EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC || numValue > EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_METRIC) { 
                eventSpeedThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_km_h, EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC, EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_METRIC);
            }
        }

        // check if need to clear the "apply" button when the value is changed back to the value in the cloud
        const sanitizedNumberRounded = numValue.toFixed(1); // rounded to whole number
        const eventSpeedThresholdRounded = Number(this.props.eventSpeedThreshold).toFixed(1);

        if (sanitizedNumberRounded === eventSpeedThresholdRounded) {
            if (indexOfDecimal === -1 ||
                indexOfDecimal < trimmedValue.length - 1) { // allow trailing decimal during input
                this.setState({
                    eventSpeedThresholdValidationMessage: undefined,
                    eventSpeedThreshold: undefined
                });
                return;
            }
        }

        this.setState({
            eventSpeedThreshold: trimmedValue, // allow trailing decimal during input
            eventSpeedThresholdValidationMessage: eventSpeedThresholdValidationMessage
        });
    }

    applyEventThresholdState = () => {
        clearTimeout(this.applyEventThresholdTimeoutId);

        // validate this attempt to update threshold
        if (isNaN(this.state.eventThreshold)) { return; }

        let eventThreshold = Number(this.state.eventThreshold);
        eventThreshold = Math.max(eventThreshold, IDLING_MIN_VAL_GRACE_PERIOD); // floor at 0 minutes
        eventThreshold = Math.min(eventThreshold, IDLING_MAX_VAL_GRACE_PERIOD); // ceiling at 60 minutes

        this.props.applyEventThreshold(
            minutesToMs(eventThreshold), // new
            this.props.eventThreshold // old (for undo)
        )
        .then(
            () => {
                this.discardEventThresholdChanges(); // clear state to hide apply/discard buttons
                clearTimeout(this.applyEventThresholdTimeoutId);
            },
            () => {clearTimeout(this.applyEventThresholdTimeoutId)}
        )
    }

    applyEventSpeedThresholdState = () => {
        clearTimeout(this.applyEventSpeedThresholdTimeoutId);
        
        // validate this attempt to update Speed threshold
        if (isNaN(this.state.eventSpeedThreshold)) { return; }

        let eventSpeedThreshold = this.state.eventSpeedThreshold;
        
        if (this.props.unitOfMeasureDistanceIsMetric !== true) {
            eventSpeedThreshold = eventSpeedThreshold * 1.609;
            if (eventSpeedThreshold < EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC) {
                eventSpeedThreshold = EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC;
            }
        }
    
        eventSpeedThreshold = Math.round(eventSpeedThreshold);

        this.props.applyEventSpeedThreshold(
            Number(eventSpeedThreshold),
            this.props.eventSpeedThreshold,
        )
        .then(
            () => {
                this.discardEventSpeedThresholdChanges();
                clearTimeout(this.applyEventSpeedThresholdTimeoutId);
            },
            () => {clearTimeout(this.applyEventSpeedThresholdTimeoutId)}
        )
    }

    discardEventThresholdChanges = () => {
        this.setState({
            eventThreshold: undefined,
            eventThresholdValidationMessage: undefined
        });
    }

    discardEventSpeedThresholdChanges = () => {
        this.setState({
            eventSpeedThreshold: undefined,
            eventSpeedThresholdValidationMessage: undefined
        });
    }

    render() {
        if (!this.props.eventConfigAvailable) {
            return null;
        }

        // show apply button(s)?
        const showThresholdApplyButton = this.state.eventThreshold !== undefined ? true : false;
        const thresholdFormActionsClassName = showThresholdApplyButton ? "form-actions" : "form-actions collapsed";
        
        /*
        const showSpeedThresholdApplyButton = this.state.eventSpeedThreshold !== undefined ? true : false;
        const speedThresholdFormActionsClassName = showSpeedThresholdApplyButton ? "form-actions" : "form-actions collapsed";
        */


        // determine which values to show in the input fields
        let eventThreshold = "";
        if (this.props.eventThreshold !== undefined) {
            eventThreshold = msToMinutes(this.props.eventThreshold); // convert from ms to minutes
        }
        if (this.state.eventThreshold !== undefined) { // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            eventThreshold = this.state.eventThreshold ? this.state.eventThreshold  : "";
        }

        /*
        let eventSpeedThreshold = "";
        if (this.props.eventSpeedThreshold !== undefined) {
            eventSpeedThreshold = this.props.eventSpeedThreshold;
        }
        if (this.state.eventSpeedThreshold !== undefined) { // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            eventSpeedThreshold = this.state.eventSpeedThreshold ? this.state.eventSpeedThreshold : "";
        }

        // determine which unit of measure to show in the input label
        let speedThresholdConstantName = this.props.unitOfMeasureDistanceIsMetric !== true ? strings.settings_global_events_speeding_threshold_type_constant_imperial : strings.settings_global_events_speeding_threshold_type_constant_metric;
        */

        return (
            <div className='setting-group-horiz'>
                <div className={this.props.eventsIdlingExpandReferenceIsExpanded ? "subgroup" : "subgroup collapsed"}>
                    <h5 className="subgroup-header">
                        <button disabled={true} // SUBSECTION_COLLAPSING_DISABLED
                            className={this.props.eventsIdlingExpandReferenceIsExpanded ? "settings-disclosure-wrapper" : "settings-disclosure-wrapper collapsed"}
                            onClick={() => {
                                this.props.expandOrCollapseStateForUIReferences(
                                    !this.props.eventsIdlingExpandReferenceIsExpanded,
                                    [this.props.eventsIdlingExpandReference])
                            }}>
                            {strings.settings_global_events_idling_header}
                            <i className="material-icons disclosure">keyboard_arrow_up</i>
                        </button>
                    </h5>

                    {/* Event Availability Toggle */}
                    <h6 className="subgroup-subheader">
                        <div className='setting-group switch-group'>
                            <div>
                                <Switch
                                    onChange={() => {

                                        clearTimeout(this.applyEventAvailabilityTimeoutId);
                                        const eventAvailabilityEnabled = this.props.eventAvailabilityEnabled;
                                        this.applyEventAvailabilityTimeoutId = setTimeout(() => {this.props.timeoutEventAvailability(eventAvailabilityEnabled)}, UPDATE_SETTINGS_TIMEOUT);

                                        this.props.applyEventAvailability(!this.props.eventAvailabilityEnabled)
                                        .then(
                                            () => {clearTimeout(this.applyEventAvailabilityTimeoutId)},
                                            () => {clearTimeout(this.applyEventAvailabilityTimeoutId)}
                                        )
                                    }}
                                    checked={this.props.eventAvailabilityEnabled || false}
                                    disabled={this.props.eventAvailabilityPending}
                                    smallFormat={true}
                                />
                                <label disabled={this.props.eventAvailabilityPending}>{this.props.eventAvailabilityEnabled?
                                    strings.settings_global_events_idling_are_enabled
                                :
                                    strings.settings_global_events_idling_are_disabled
                                }</label>
                                {this.props.eventAvailabilityPending ? <ProgressOverlay /> : null }
                            </div>
                            <div className="error-message">
                                {this.props.eventAvailabilityErrorMessage}
                            </div>
                        </div>
                    </h6>

                    {/* Event Threshold (Grace Period) Input */}
                    <div className='setting-group threshold-slider'>
                        <div className="description">
                            {strings.settings_global_events_idling_grace_period_description}
                        </div>
                        <div className="form">
                            <div>
                                <input type="text" autoComplete="off" className="threshold" value={eventThreshold} placeholder={strings.settings_global_events_threshold_placeholder} onChange={this.handleThresholdChange}></input>
                            </div>
                            <div>
                                <label className="inline-label-large">
                                    {strings.settings_global_events_time_type_minutes}
                                </label>
                            </div>
                        </div>
                        <div className={thresholdFormActionsClassName}>
                            <button className="btn btn-blue" disabled={this.state.eventThresholdValidationMessage !== undefined} onClick={this.applyEventThresholdState}>{strings.action_apply}</button>
                            <button className="btn btn-outline-red" onClick={this.discardEventThresholdChanges}>{strings.settings_action_discard_changes}</button>
                        </div>
                        <div className="error-message form-indent">
                            {this.props.eventThresholdErrorMessage || this.state.eventThresholdValidationMessage}
                        </div>
                        {this.props.eventThresholdPending ? <ProgressOverlay /> : null }
                    </div>

                    {/* Event Speed Threshold (Speed floor) Input */}
                    {/*
                    <div className='setting-group threshold-slider'>
                        <div className="description">
                            {strings.settings_global_events_idling_speed_floor_description}
                        </div>
                        <div className="form">
                            <div>
                                <input type="text" autoComplete="off" className="threshold" value={eventSpeedThreshold} placeholder={strings.settings_global_events_speeding_threshold_placeholder} onChange={this.handleSpeedThresholdChange}></input>
                            </div>
                            <div>
                                <label className="inline-label-large">
                                    {speedThresholdConstantName}
                                </label>
                            </div>
                        </div>
                        <div className={speedThresholdFormActionsClassName}>
                            <button className="btn btn-blue" disabled={this.state.eventSpeedThresholdValidationMessage !== undefined} onClick={this.applyEventSpeedThresholdState}>{strings.action_apply}</button>
                            <button className="btn btn-outline-red" onClick={this.discardEventSpeedThresholdChanges}>{strings.settings_action_discard_changes}</button>
                        </div>
                        <div className="error-message form-indent">
                            {this.props.eventSpeedThresholdErrorMessage || this.state.eventSpeedThresholdValidationMessage}
                        </div>
                        {this.props.eventSpeedThresholdPending || this.props.unitOfMeasureDistancePending? <ProgressOverlay /> : null }
                    </div> */}
                </div>
            </div>
        );
    }
}